import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  NoteGenerationFailedEventProps,
  OfflineRecordingDeletedEventProps,
  OfflineRecordingQueuedEventProps,
  OfflineRecordingStartedEventProps,
  OfflineSessionDeleteEnqueueFailedEventProps,
  OfflineSessionDeleteQueuedEventProps,
  OfflineSessionDeleteReplayFailedEventProps,
  OfflineSyncCompletedEventProps,
  OfflineSyncFailedEventProps,
} from '@/models/analytics/features/offline-recording';

const offlineAnalyticsOptions = { amplitude: false } as const;

export function trackOfflineRecordingStarted(
  props: OfflineRecordingStartedEventProps
): void {
  track({
    event_name: TrackingEventName.OfflineRecordingStarted,
    props,
    options: offlineAnalyticsOptions,
  });
}

export function trackOfflineRecordingQueued(
  props: OfflineRecordingQueuedEventProps
): void {
  track({
    event_name: TrackingEventName.OfflineRecordingQueued,
    props,
    options: offlineAnalyticsOptions,
  });
}

export function trackOfflineRecordingDeleted(
  props: OfflineRecordingDeletedEventProps
): void {
  track({
    event_name: TrackingEventName.OfflineRecordingDeleted,
    props,
    options: offlineAnalyticsOptions,
  });
}

export function trackOfflineSyncCompleted(props: OfflineSyncCompletedEventProps): void {
  track({
    event_name: TrackingEventName.OfflineSyncCompleted,
    props,
    options: offlineAnalyticsOptions,
  });
}

export function trackOfflineSyncFailed(props: OfflineSyncFailedEventProps): void {
  track({
    event_name: TrackingEventName.OfflineSyncFailed,
    props,
    options: offlineAnalyticsOptions,
  });
}

/**
 * The three queued-delete events below reach Datadog only for now: their Iglu
 * schemas are not published yet, so `src/lib/snowplow/events/offline-recording.ts`
 * has no handler for them and the Snowplow leg is a no-op. Registering a
 * handler against an unpublished schema would not warehouse them — it would
 * fail Iglu validation and divert every one into the bad-events stream, which
 * is worse than the no-op because it looks like it works.
 *
 * This is a gap to CLOSE, not a shape to copy. It is the opposite of
 * `offline_sync_deferred`, which is sometimes cited alongside it: that event
 * has its schema (`iglu:com.heidi/offline_sync_deferred/jsonschema/1-0-0`) and
 * a generated `trackOfflineSyncDeferredSpec`, and is waiting on an emitter.
 * These three are emitters waiting on a schema. Publishing the schemas and
 * regenerating Snowtype is what unblocks them, and the handler is registered in
 * that same change.
 */
export function trackOfflineSessionDeleteQueued(
  props: OfflineSessionDeleteQueuedEventProps
): void {
  track({
    event_name: TrackingEventName.OfflineSessionDeleteQueued,
    props,
    options: offlineAnalyticsOptions,
  });
}

export function trackOfflineSessionDeleteEnqueueFailed(
  props: OfflineSessionDeleteEnqueueFailedEventProps
): void {
  track({
    event_name: TrackingEventName.OfflineSessionDeleteEnqueueFailed,
    props,
    options: offlineAnalyticsOptions,
  });
}

export function trackOfflineSessionDeleteReplayFailed(
  props: OfflineSessionDeleteReplayFailedEventProps
): void {
  track({
    event_name: TrackingEventName.OfflineSessionDeleteReplayFailed,
    props,
    options: offlineAnalyticsOptions,
  });
}

export function trackOfflineNoteGenerationFailed(
  props: NoteGenerationFailedEventProps
): void {
  track({
    event_name: TrackingEventName.NoteGenerationFailed,
    props,
    options: offlineAnalyticsOptions,
  });
}
